import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import MeaPermissionGuard from "../../../components/MeaPermissionGuard";
import { PERMISSION_TRAINING_TEACHERS } from "../../../permissions";
import teachersAPI from "../../../services/teachers";
import sessionsAPI from "../../../services/sessions";
import trainingsAPI from "../../../services/trainings";
import { getTeacherMonthlyStats } from "../../trainings/teachers/teacherSessionStats";

const PrintAllTeachersContent = () => {
  const { schoolId, trainingId } = useParams();
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get("teacherMonth");
  const allMonths = searchParams.get("printPeriod") === "all";
  const [generatedAt] = useState(() => moment.tz("Europe/Paris").format("DD/MM/YYYY [à] HH:mm:ss"));
  const month = moment(monthParam, "YYYY-MM", true);
  const validMonth = month.isValid();
  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, "");
  const training = trainingQuery.data?.data;
  const months = useMemo(() => {
    if (!allMonths) return validMonth ? [monthParam] : [];
    if (!training?.start_courses_date || !training?.end_courses_date) return [];
    const start = moment.tz(training.start_courses_date, "Europe/Paris").startOf("month");
    const end = moment.tz(training.end_courses_date, "Europe/Paris").startOf("month");
    const result = [];
    while (start.isSameOrBefore(end, "month")) {
      result.push(start.format("YYYY-MM"));
      start.add(1, "month");
    }
    return result;
  }, [allMonths, validMonth, monthParam, training?.start_courses_date, training?.end_courses_date]);
  useEffect(() => {
    if (!training?.name || months.length === 0) return;
    const previousTitle = document.title;
    const period = allMonths
      ? `${months[0]} au ${months[months.length - 1]}`
      : months[0];
    document.title = `Liste des enseignants - ${training.name} - ${period}`;
    return () => {
      document.title = previousTitle;
    };
  }, [training?.name, months, allMonths]);
  const filter = useMemo(() => {
    return {
      start_date: months.length ? moment(months[0], "YYYY-MM").startOf("month").format("YYYY-MM-DD") : "",
      end_date: months.length ? moment(months[months.length - 1], "YYYY-MM").endOf("month").format("YYYY-MM-DD") : "",
    };
  }, [months]);
  const teachersQuery = teachersAPI.useGetList(schoolId, trainingId, "", 1, 100000);
  const sessionsQuery = sessionsAPI.useGetList(
    months.length ? schoolId : undefined, trainingId, "", 1, 100000, filter
  );

  if (!allMonths && !validMonth) return <div role="alert">Le mois sélectionné est invalide.</div>;
  if (teachersQuery.isError || sessionsQuery.isError || trainingQuery.isError) {
    return <div role="alert">Erreur lors du chargement de la liste des enseignants à imprimer.</div>;
  }
  if (allMonths && training && months.length === 0) {
    return <div role="alert">Les dates de la période de formation sont manquantes ou invalides.</div>;
  }
  if (!teachersQuery.data || !sessionsQuery.data || !trainingQuery.data || teachersQuery.isFetching || sessionsQuery.isFetching) {
    return <div role="status">Chargement...</div>;
  }

  return createPortal(
    <div className="teachers-print-page">
      <style>{`
        body:has(> .teachers-print-page) > #root { display: none; }
        .teachers-print-page { font-family: Arial, sans-serif; padding: 20px; background: white; color: black; min-height: 100vh; }
        .teachers-print-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .teachers-print-table th, .teachers-print-table td { border: 1px solid #333; padding: 8px; text-align: left; }
        .teachers-print-table th { background: #f5f5f5; }
        .teachers-print-table tr:nth-child(even) { background: #f9f9f9; }
        .teachers-print-month { margin-bottom: 30px; }
        .teachers-print-footer { font-size: 9px; color: #666; text-align: right; margin-top: 20px; }
        .teachers-print-button { position: fixed; top: 90px; right: 20px; background: #007bff; color: white; border: none; padding: 15px 30px; border-radius: 8px; cursor: pointer; font-size: 20px; font-weight: bold; z-index: 1000; }
        @media print {
          @page { size: A4 landscape; margin: 20mm 20mm 25mm; }
          body:has(> .teachers-print-page) > :not(.teachers-print-page) { display: none !important; }
          html, body { margin: 0; padding: 0; background: white !important; }
          .teachers-print-page { display: block; padding: 0; min-height: auto; }
          .teachers-print-button { display: none; }
          .teachers-print-month { display: flex; flex-direction: column; box-sizing: border-box; width: 100%; min-height: 160mm; padding: 5mm 15mm; margin: 0; }
          .teachers-print-month h1 { font-size: 18px; margin: 0 0 8px; }
          .teachers-print-month h2 { font-size: 14px; margin: 0 0 8px; }
          .teachers-print-month p { font-size: 12px; margin: 0 0 12px; }
          .teachers-print-month + .teachers-print-month { break-before: page; }
          .teachers-print-footer { margin-top: auto; padding-top: 5mm; break-inside: avoid; }
          .teachers-print-table thead { display: table-header-group; }
          .teachers-print-table tr { break-inside: avoid; }
          .teachers-print-table { font-size: 11px; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .teachers-print-table th, .teachers-print-table td { padding: 5px; }
        }
      `}</style>
      <button className="teachers-print-button" onClick={() => window.print()}>
        <i className="pi pi-print mr-2" aria-hidden="true" />
        Imprimer
      </button>
      {months.map((monthValue) => {
        const monthlySessions = sessionsQuery.data.data.filter((session) =>
          session.event?.start_date &&
          moment.tz(session.event.start_date, "Europe/Paris").format("YYYY-MM") === monthValue
        );
        const teachers = teachersQuery.data.data.map((teacher) =>
          getTeacherMonthlyStats(teacher, monthlySessions)
        );
        const monthLabel = moment(monthValue, "YYYY-MM").locale("fr").format("MMMM YYYY");
        return (
          <section className="teachers-print-month" key={monthValue} aria-label={monthLabel}>
            <h1>Liste des enseignant(e)s</h1>
            <h2>{training.name}</h2>
            <p>{monthLabel}</p>
            {teachers.length === 0 ? <p>Aucun enseignant dans cette formation.</p> : (
              <table className="teachers-print-table">
                <thead>
                  <tr>
                    <th>Enseignant(e)</th>
                    <th>Nombre de cours par semaine</th>
                    <th>Séances prévues du mois</th>
                    <th>Séances effectives du mois</th>
                    <th>Total de points du mois</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher) => (
                    <tr key={teacher.id}>
                      <td>{teacher.member?.name || [teacher.member?.lastname, teacher.member?.firstname].filter(Boolean).join(" ")}</td>
                      <td>{teacher.schedule_items?.length || 0}</td>
                      <td>{teacher.planned_sessions_count}</td>
                      <td>{teacher.effective_sessions_count}</td>
                      <td>{new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(teacher.effective_points_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <footer className="teachers-print-footer">Généré le {generatedAt} (Europe/Paris)</footer>
          </section>
        );
      })}
    </div>,
    document.body
  );
};

const PrintAllTeachersPage = () => {
  const { schoolId, trainingId } = useParams();
  const { pathname } = useLocation();
  const fallbackTo = pathname.includes("/secretariat/")
    ? `/school/${schoolId}/secretariat/teachers/${trainingId}`
    : `/school/${schoolId}/admin/trainings/${trainingId}/teachers`;
  return (
    <MeaPermissionGuard
      permission={PERMISSION_TRAINING_TEACHERS.LIST}
      fallbackTo={fallbackTo}
    >
      <PrintAllTeachersContent />
    </MeaPermissionGuard>
  );
};

export default PrintAllTeachersPage;
